<div class="pool-config-edit-container p-8 bg-slate-50 min-h-full">

    <!-- Header -->
    <div class="flex items-start justify-between mb-6">
        <div>
            <button type="button" (click)="goBack()"
                class="text-xs text-slate-400 hover:text-slate-600 transition-colors inline-flex items-center gap-1 mb-1">
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to organization
            </button>
            <h1 class="text-2xl font-bold text-slate-900">{{ config()?.name || 'Pool Config' }}</h1>
            <p class="text-sm text-slate-500 mt-0.5">Manage members, zones and access rules for this pool configuration.</p>
        </div>
    </div>

    <!-- Loading -->
    @if (loading()) {
    <div class="flex justify-center items-center py-16">
        <div class="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-blue-600"></div>
    </div>
    }

    <!-- Messages -->
    @if (errorMessage()) {
    <div class="mb-4">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="errorMessage.set(null)">{{ errorMessage() }}</jjames-alert>
    </div>
    }
    @if (successMessage()) {
    <div class="mb-4">
        <jjames-alert type="success" [dismissible]="true" (dismissed)="successMessage.set(null)">{{ successMessage() }}</jjames-alert>
    </div>
    }

    @if (!loading() && config()) {

    <!-- No-zones warning. Keyed on this config's own zones: the message says "this pool config",
         and it used to ask whether the organization had any zone at all, so a config with none
         selected inside a zone-owning org showed nothing. -->
    @if (hasNoAllowedZones) {
    <div class="mb-6">
        <jjames-alert type="warning">
            This pool config has no allowed zones. The classifier will not match any parking entry until at least one zone is added.
        </jjames-alert>
    </div>
    }

    <!-- Summary stat row -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
            <div class="text-xs text-slate-500 mb-1.5">Status</div>
            @if (config()?.isActive) {
            <span class="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-medium">Active</span>
            } @else {
            <span class="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full font-medium">Inactive</span>
            }
        </div>
        <div class="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
                <div class="text-2xl font-bold text-slate-900">{{ config()?.maxCarsAllowed ?? '—' }}</div>
                <div class="text-xs text-slate-500 mt-1">Max Cars</div>
            </div>
            <div class="bg-blue-50 text-blue-600 p-3 rounded-lg">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h6m-6 4h6m-3 8v-5" />
                </svg>
            </div>
        </div>
        <div class="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
                <div class="text-2xl font-bold text-slate-900">{{ assignedParkers.length }}</div>
                <div class="text-xs text-slate-500 mt-1">Members</div>
            </div>
            <div class="bg-blue-50 text-blue-600 p-3 rounded-lg">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </div>
        </div>
        <div class="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
                <div class="text-2xl font-bold text-slate-900">{{ allowedZones.length }}</div>
                <div class="text-xs text-slate-500 mt-1">Allowed Zones</div>
            </div>
            <div class="bg-blue-50 text-blue-600 p-3 rounded-lg">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
            </div>
        </div>
    </div>

    <!-- Main 2-column dashboard -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">

        <!-- Left column: details + zones -->
        <div class="space-y-6">

            <!-- Configuration Details -->
            <div class="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-base font-semibold text-slate-900">Configuration</h2>
                    @if (!showEditForm()) {
                    <button type="button" (click)="startEdit()" class="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                    </button>
                    }
                </div>

                @if (showEditForm()) {
                <!-- Edit form -->
                <div class="space-y-4">
                    <div>
                        <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Name <span class="text-red-500">*</span></label>
                        <input type="text" [value]="editName()" (input)="editName.set($any($event.target).value)"
                            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Max Cars Allowed</label>
                        <input type="number" min="0" [value]="editMaxCars() ?? ''"
                            (input)="editMaxCars.set($any($event.target).value ? +$any($event.target).value : null)"
                            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Description</label>
                        <textarea rows="2" [value]="editDescription()" (input)="editDescription.set($any($event.target).value)"
                            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"></textarea>
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Valid From</label>
                            <input type="date" [value]="editValidFrom()" (input)="editValidFrom.set($any($event.target).value)"
                                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                        </div>
                        <div>
                            <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Valid To</label>
                            <input type="date" [value]="editValidTo()" (input)="editValidTo.set($any($event.target).value)"
                                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                        </div>
                    </div>

                    <!-- Overflow behavior (pool full). This is the ONLY setting that decides who
                         pays: a stay inside capacity is free to the parker by construction, because
                         the organization paid for its places up front. -->
                    <div>
                        <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">When the pool is full</label>
                        <p class="text-xs text-slate-500 mb-1.5">Stays inside capacity are always free to the parker. This decides who pays beyond it.</p>
                        <select [value]="editOverflowBehavior()" (change)="editOverflowBehavior.set($any($event.target).value)"
                            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                            @for (o of overflowBehaviors; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
                        </select>
                        @for (o of overflowBehaviors; track o.value) {
                            @if (editOverflowBehavior() === o.value) { <p class="mt-1 text-xs text-slate-500">{{ o.hint }}</p> }
                        }
                    </div>

                    <app-access-window-field [(value)]="editWindow"
                        enabledLabel="Limit pool access to a daily time window"
                        description="Pool members may only enter during these hours (camera-local). Outside them they fall through to visitor handling. Off = 24/7."
                        defaultStart="06:00" defaultEnd="17:00" />

                    <div class="flex gap-3 pt-1">
                        <button type="button" (click)="saveEdit()" [disabled]="editLoading()"
                            class="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-blue-700 transition disabled:opacity-60">
                            {{ editLoading() ? 'Saving...' : 'Save' }}
                        </button>
                        <button type="button" (click)="cancelEdit()" [disabled]="editLoading()"
                            class="border border-slate-200 bg-white text-slate-700 px-4 py-2 rounded-lg font-medium text-sm hover:bg-slate-50 transition">Cancel</button>
                    </div>
                </div>
                } @else {
                <!-- Static view -->
                <dl class="divide-y divide-slate-100">
                    <div class="flex justify-between py-2.5">
                        <dt class="text-xs font-semibold text-slate-500 uppercase">Description</dt>
                        <dd class="text-sm text-slate-900 text-right max-w-[60%]">{{ config()?.description || '—' }}</dd>
                    </div>
                    <div class="flex justify-between py-2.5">
                        <dt class="text-xs font-semibold text-slate-500 uppercase">Valid From</dt>
                        <dd class="text-sm text-slate-900">{{ config()?.validFrom | date:'mediumDate' }}</dd>
                    </div>
                    <div class="flex justify-between py-2.5">
                        <dt class="text-xs font-semibold text-slate-500 uppercase">Valid To</dt>
                        <dd class="text-sm text-slate-900">{{ config()?.validTo ? (config()!.validTo | date:'mediumDate') : 'No expiry' }}</dd>
                    </div>
                    <div class="flex justify-between py-2.5">
                        <dt class="text-xs font-semibold text-slate-500 uppercase">When Pool Full</dt>
                        <dd class="text-sm text-slate-900">{{ overflowBehaviorLabel(config()?.overflowBehavior) }}</dd>
                    </div>
                    <div class="flex justify-between py-2.5 gap-3">
                        <dt class="text-xs font-semibold text-slate-500 uppercase shrink-0">Access Window</dt>
                        <dd class="text-sm text-slate-900 text-right">{{ windowSummary }}</dd>
                    </div>
                </dl>
                }
            </div>

            <!-- Allowed Zones -->
            <div class="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                <h2 class="text-base font-semibold text-slate-900 mb-1">Allowed Zones</h2>
                <p class="text-xs text-slate-500 mb-4">Zones this pool config is valid for.</p>

                <!-- Add zone -->
                <div class="flex items-center gap-2 mb-4">
                    <select class="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition disabled:bg-slate-50"
                        [value]="selectedZoneIdToAdd()" (change)="selectedZoneIdToAdd.set($any($event.target).value)"
                        [disabled]="availableZoneIds().length === 0 || zoneActionLoading()">
                        @if (availableZoneIds().length === 0) {
                        <option value="">All zones assigned</option>
                        } @else {
                        <option value="">— Select zone —</option>
                        @for (zone of availableZoneIds(); track zone.parkingZoneId) {
                        <option [value]="zone.parkingZoneId">{{ getZoneName(zone.parkingZoneId!) }}</option>
                        }
                        }
                    </select>
                    <button type="button" (click)="addZone()" [disabled]="!selectedZoneIdToAdd() || zoneActionLoading()"
                        class="bg-blue-600 text-white px-3 py-2 rounded-lg font-medium text-sm hover:bg-blue-700 transition disabled:opacity-50 shrink-0">Add</button>
                </div>

                <!-- Zone rows -->
                @if (allowedZones.length > 0) {
                @for (zone of allowedZones; track zone.id) {
                <div class="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100 mb-2">
                    <div class="min-w-0">
                        <p class="text-sm font-medium text-slate-900 truncate">{{ zone.name || '—' }}</p>
                        <p class="font-mono text-[10px] text-slate-400 mt-0.5 truncate">{{ zone.id }}</p>
                    </div>
                    @if (deleteZoneConfirmId() === zone.id) {
                    <div class="inline-flex items-center gap-1.5 shrink-0">
                        <span class="text-xs text-red-600">Remove?</span>
                        <button type="button" (click)="removeZone(zone.id!)" class="bg-red-600 text-white px-2.5 py-1 rounded-md text-xs hover:bg-red-700">Yes</button>
                        <button type="button" (click)="cancelRemoveZone()" class="border border-slate-200 text-slate-600 px-2.5 py-1 rounded-md text-xs hover:bg-white">No</button>
                    </div>
                    } @else {
                    <button type="button" (click)="confirmRemoveZone(zone.id!)" [disabled]="zoneActionLoading()"
                        class="text-slate-400 hover:text-red-600 text-xs font-medium shrink-0">Remove</button>
                    }
                </div>
                }
                } @else {
                <p class="text-sm text-slate-400 italic">No zones assigned yet.</p>
                }
            </div>
        </div>

        <!-- Right column: members (span 2) -->
        <div class="xl:col-span-2 bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
            <div class="flex items-start justify-between mb-1">
                <h2 class="text-base font-semibold text-slate-900">Pool Members</h2>
            </div>
            <p class="text-xs text-slate-500 mb-4">Adding a parker auto-creates a parking right covering this pool's zones for the validity period.</p>

            @if (hasNoAllowedZones) {
            <div class="mb-4">
                <jjames-alert type="warning">
                    @if (hasNoZones) {
                    Add at least one zone to the organization before assigning parkers.
                    } @else {
                    Select at least one allowed zone before assigning parkers.
                    }
                </jjames-alert>
            </div>
            }

            <!-- Add parkers. Multi-select on purpose: each add is its own POST that also generates a
                 parking right, and the list used to reload the whole config after every single one.
                 Operators fill pools a dozen parkers at a time. -->
            <div class="mb-5">
                <div class="flex items-start gap-2">
                    <select multiple size="6"
                        class="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition disabled:bg-slate-50"
                        (change)="onParkerSelectionChange($event)"
                        [disabled]="availableParkers().length === 0 || hasNoAllowedZones || parkerActionLoading()">
                        @for (p of availableParkers(); track p.id) {
                        <!--
                          Name first, plate as the fallback, id only as a last resort. This used to show
                          the plate or a raw UUID because ParkerDto carried no name — picking a colleague
                          out of a list of GUIDs is not something an operator can reasonably do.
                        -->
                        <option [value]="p.id" [selected]="selectedParkerIdsToAdd().includes(p.id!)">{{ parkerOptionLabel(p) }}</option>
                        }
                    </select>
                    <div class="flex flex-col gap-2 shrink-0">
                        <button type="button" (click)="addSelectedParkers()"
                            [disabled]="selectedParkerIdsToAdd().length === 0 || hasNoAllowedZones || parkerActionLoading()"
                            class="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-blue-700 transition disabled:opacity-50 inline-flex items-center gap-2">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            @if (parkerActionLoading()) { Adding {{ addProgress() }} } @else { Add
                            @if (selectedParkerIdsToAdd().length > 0) { ({{ selectedParkerIdsToAdd().length }}) } }
                        </button>
                        <button type="button" (click)="selectAllAvailableParkers()"
                            [disabled]="availableParkers().length === 0 || hasNoAllowedZones || parkerActionLoading()"
                            class="border border-slate-200 text-slate-600 px-4 py-2 rounded-lg font-medium text-sm hover:bg-slate-50 transition disabled:opacity-50">
                            Select all
                        </button>
                    </div>
                </div>
                @if (hasNoAllowedZones) {
                <p class="text-xs text-amber-600 mt-2">
                    @if (hasNoZones) {
                    Add a zone to the organization before assigning parkers.
                    } @else {
                    Select an allowed zone first.
                    }
                </p>
                } @else if (availableParkers().length === 0) {
                <p class="text-xs text-slate-500 mt-2">All org parkers are already in this pool.</p>
                } @else {
                <p class="text-xs text-slate-500 mt-2">Ctrl/Cmd-click or shift-click to pick several.</p>
                }
            </div>

            <!-- Members table -->
            @if (hasNoParkers) {
            <div class="text-center py-12 border border-dashed border-slate-200 rounded-xl">
                <p class="text-sm text-slate-400 italic">No parkers assigned yet.</p>
            </div>
            } @else {
            <div class="overflow-x-auto rounded-xl border border-slate-100">
                <table class="min-w-full">
                    <thead class="bg-slate-50">
                        <tr>
                            <th class="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Parker</th>
                            <th class="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Type</th>
                            <th class="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">License Plates</th>
                            <th class="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (parker of assignedParkers; track parker.id) {
                        <tr class="border-b border-slate-100 hover:bg-slate-50/50 transition">
                            <td class="p-4">
                                <p class="text-sm font-medium text-slate-900">{{ parker.firstName }} {{ parker.lastName }}</p>
                                @if (parker.email) { <p class="text-xs text-slate-500">{{ parker.email }}</p> }
                            </td>
                            <td class="p-4">
                                @if (parker.parkerType) {
                                <span class="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full font-medium">{{ parker.parkerType }}</span>
                                } @else { <span class="text-xs text-slate-400">—</span> }
                            </td>
                            <td class="p-4">
                                @if ((parker.activeLicenses ?? []).length > 0) {
                                <div class="flex flex-wrap gap-1.5">
                                    @for (plate of (parker.activeLicenses ?? []); track plate.id) {
                                    <span class="bg-slate-900 text-white font-mono text-xs px-2 py-1 rounded tracking-wide shadow-sm">{{ plate.fullNumber }}</span>
                                    }
                                </div>
                                } @else { <span class="text-xs text-slate-400 italic">No plates</span> }
                            </td>
                            <td class="p-4 text-right">
                                @if (deleteParkerConfirmId() === parker.id) {
                                <div class="inline-flex items-center gap-1.5">
                                    <span class="text-xs text-red-600">Remove?</span>
                                    <button type="button" (click)="removeParker(parker.id!)" class="bg-red-600 text-white px-2.5 py-1 rounded-md text-xs hover:bg-red-700">Yes</button>
                                    <button type="button" (click)="cancelRemoveParker()" class="border border-slate-200 text-slate-600 px-2.5 py-1 rounded-md text-xs hover:bg-white">No</button>
                                </div>
                                } @else {
                                <button type="button" (click)="confirmRemoveParker(parker.id!)" [disabled]="parkerActionLoading()"
                                    class="text-slate-400 hover:text-red-600 text-xs font-medium">Remove</button>
                                }
                            </td>
                        </tr>
                        }
                    </tbody>
                </table>
            </div>
            }
        </div>
    </div>

    }
</div>
